<script setup lang="ts">
import type { Slots, ButtonHTMLAttributes } from 'vue';

import { ref, computed, nextTick } from 'vue';
import { Loader as LucideLoader } from 'lucide-vue-next';

interface Props extends /* @vue-ignore */ ButtonHTMLAttributes {
  label?: string;
  lucideIcon?: any; // This will be a Lucide icon component
  loading?: boolean;
  iconColor?: string;
  tooltip?: string | null;
  imageSrc?: string | null;
  size?: 'sm' | 'md' | 'lg';
  iconPosition?: 'left' | 'right';
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'naked' | 'elevated' | 'subtle';
}

const {
  label,
  loading,
  iconColor,
  lucideIcon,
  size = 'md',
  tooltip = null,
  imageSrc = null,
  variant = 'primary',
  iconPosition = 'left',
} = defineProps<Props>();

const hasIcon = lucideIcon;

const slots: Slots = useSlots();

const hasText = computed(() => {
  return label !== undefined || slots.default !== undefined;
});

const commonClasses = [
  'relative flex items-center justify-center rounded-lg transition',
  'disabled:cursor-not-allowed disabled:opacity-50',
];

const variantClasses = computed(() => {
  switch (variant) {
    case 'primary':
      return [
        'bg-#009883 hover:bg-#006856 text-white',
        'outline-unset focus-visible:outline-1 focus-visible:outline outline-emerald-500 outline-offset-2',
        'disabled:hover:bg-emerald-500',
      ];
    case 'secondary':
      return [
        'bg-neutral-600 hover:bg-neutral-700 text-white',
        'outline-unset focus-visible:outline-1 focus-visible:outline outline-neutral-600 outline-offset-2',
        'disabled:hover:bg-neutral-600',
      ];
    case 'outline':
      return [
        'hover:bg-emerald-950 text-emerald-500',
        'outline outline-1 focus-visible:outline-offset-2 focus-visible:outline-emerald-300 outline-emerald-500',
        'disabled:bg-transparent',
      ];
    case 'ghost':
      return [
        'hover:bg-black/5',
        'outline-unset focus-visible:outline-1 focus-visible:outline outline-black/50 outline-offset-2',
        'disabled:bg-transparent',
      ];
    case 'naked':
      return ['outline-unset p-0!'];
    case 'elevated':
      return ['bg-white hover:bg-gray-100 text-gray-800 font-semibold py-2 px-4 border border-gray-400 rounded shadow'];
    case 'subtle':
      return [
        'text-emerald-700',
        'bg-white hover:bg-emerald-50',
        'border border-emerald-300/80 hover:border-emerald-400',
        'rounded-full',
        'shadow-[0_1px_0_0_rgba(16,24,40,0.04),0_1px_2px_0_rgba(16,24,40,0.06)]',
        'focus-visible:outline-2 focus-visible:outline-emerald-400 outline-offset-2',
      ];
    default:
      return '';
  }
});

const sizeClasses = computed(() => {
  switch (size) {
    case 'sm':
      return [
        'text-xs gap-1',
        !hasText.value && 'p-1.5',
        iconPosition === 'left' && hasIcon && hasText.value && 'pl-2 pr-3 py-1.5',
        iconPosition === 'right' && hasIcon && hasText.value && 'pl-3 pr-2 py-1.5',
        !hasIcon && 'px-2 py-1.5',
      ];
    case 'md':
      return [
        'text-sm gap-2',
        !hasText.value && 'p-7px',
        iconPosition === 'left' && hasIcon && hasText.value && 'pl-3 pr-4 py-6px',
        iconPosition === 'right' && hasIcon && hasText.value && 'pl-4 pr-3 py-6px',
        !hasIcon && 'px-3 py-6px',
      ];
    case 'lg':
      return [
        'text-base gap-2',
        !hasText.value && 'p-10px',
        iconPosition === 'left' && hasIcon && hasText.value && 'pl-4 pr-5 py-2',
        iconPosition === 'right' && hasIcon && hasText.value && 'pl-5 pr-4 py-2',
        !hasIcon && 'px-4 py-2',
      ];
    default:
      return '';
  }
});

const iconSize = computed(() => {
  switch (size) {
    case 'sm':
      return '16';
    case 'md':
      return '18';
    case 'lg':
      return '20';
    default:
      return '';
  }
});

const imageSize = computed(() => {
  switch (size) {
    case 'sm':
      return '4';
    case 'md':
      return '5';
    case 'lg':
      return '6';
    default:
      return '';
  }
});

const showTooltip = ref(false);
const tooltipStyle = ref({});
const buttonRef = ref<HTMLElement | null>(null);

const setTooltipPosition = () => {
  if (!buttonRef.value) return;
  const rect = buttonRef.value.getBoundingClientRect();
  tooltipStyle.value = {
    position: 'fixed',
    top: `${rect.bottom + 8}px`, // 8px below the button
    transform: 'translate(-50%, 0)',
    left: `${rect.left + rect.width / 2}px`,
  };
};

const handleMouseEnter = async () => {
  await nextTick();
  setTooltipPosition();
  showTooltip.value = true;
};

const handleMouseLeave = () => {
  showTooltip.value = false;
};
</script>

<template>
  <button
    ref="buttonRef"
    type="button"
    :class="[commonClasses, sizeClasses, variantClasses]"
    @mouseenter="tooltip ? handleMouseEnter() : null"
    @mouseleave="tooltip ? handleMouseLeave() : null"
  >
    <!-- Icon/Image on the left or Spinner (if loading) -->
    <template v-if="iconPosition === 'left'">
      <component
        :is="LucideLoader"
        v-if="loading && (hasIcon || imageSrc)"
        :size="Number(iconSize)"
        class="animate-spin"
      />
      <component
        :is="lucideIcon"
        v-else-if="hasIcon && !loading"
        :size="Number(iconSize)"
        :class="iconColor"
      />
      <img
        v-else-if="imageSrc && !loading"
        :src="imageSrc"
        :class="[`w-${imageSize} h-${imageSize}`, `text-${iconColor}`]"
      >
    </template>

    <!-- Text or Slot Content -->
    <span
      v-if="hasText && (!loading || (loading && (hasIcon || imageSrc)))"
      class="max-w-full overflow-hidden truncate"
    >
      <slot>{{ label }}</slot>
    </span>

    <!-- Spinner (if loading and no icon/image) -->
    <component
      :is="LucideLoader"
      v-if="loading && !(hasIcon || imageSrc)"
      :size="Number(iconSize)"
      class="animate-spin"
    />

    <!-- Icon/Image on the right or Spinner (if loading) -->
    <template v-if="iconPosition === 'right'">
      <component
        :is="LucideLoader"
        v-if="loading && (hasIcon || imageSrc)"
        :size="Number(iconSize)"
        class="animate-spin"
      />
      <component
        :is="lucideIcon"
        v-else-if="hasIcon && !loading"
        :size="Number(iconSize)"
        :class="iconColor"
      />
      <img
        v-else-if="imageSrc && !loading"
        :src="imageSrc"
        :class="[`w-${imageSize} h-${imageSize}`, `text-${iconColor}`]"
      >
    </template>

    <!-- Tooltip -->
    <Teleport to="body">
      <div
        v-if="showTooltip && tooltip"
        class="z-50 min-w-50 rounded-lg bg-gray-800 p-2 text-xs text-white shadow-lg"
        :style="tooltipStyle"
      >
        {{ tooltip }}
      </div>
    </Teleport>
  </button>
</template>
